<template>
	<SuiSkeleton v-if="isLoading" height="4rem" width="100%" />
	<AppDropdown
		v-else
		class="user-select-list"
		is-full-width
		:popover-props="{
			placement: 'bottom-start',
			distance: 0,
		}"
	>
		<template #trigger="{ isOpen }">
			<SuiToggleButton
				as="div"
				:active="isOpen"
				icon="person"
				type="form"
				is-full-width
				:variant="variant"
				:message="message"
			>
				<SuiHorizontalTruncatingList :items="summaryText">
					<template #default="{ item }">
						<span
							class="user-select-list__summary-text"
							:class="summaryTextClassAddons"
						>
							{{ item }}
						</span>
					</template>
					<template #truncated-indicator="{ hiddenItemsCount }">
						{{
							hiddenItemsCount > 0
								? $t('select_list_users_groups_plus_more', {
										count: hiddenItemsCount,
									})
								: ''
						}}
					</template>
				</SuiHorizontalTruncatingList>
			</SuiToggleButton>
		</template>

		<SuiSelectListItemsAndGroups
			:value="modelValue"
			:items="users"
			:groups="userGroups"
			:search-placeholder="
				$t('select_list_users_groups_search_placeholder')
			"
			:items-view-title-text="$t('select_list_users_groups_items')"
			:groups-view-title-text="$t('select_list_users_groups_groups')"
			:select-all-text="$t('select_list_users_groups_select_all')"
			:empty-state-text="$t('select_list_users_groups_no_results')"
			:show-switch="showSwitch"
			:is-multiple-group-select-enabled="isMultipleGroupSelectEnabled"
			@toggle-item="onUserToggle"
			@toggle-all="onToggleAll"
			@toggle-all-groups="onToggleAllGroups"
			@toggle-group-on="onToggleGroupOn"
			@toggle-group-off="onToggleGroupOff"
			@select-group="onSelectGroup"
		/>
	</AppDropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
	SuiSkeleton,
	SuiSelectListItemsAndGroups,
	SuiToggleButton,
	type SuiSelectListItemsAndGroupsValueType,
	type SuiSelectListGroupType,
	SuiHorizontalTruncatingList,
	SuiInputVariant,
} from '@developers/scoro-ui';
import {
	type UserStatus,
	type DynamicUserGroup,
	type UserSelectListQuery,
	type User,
	useUserSelectListQuery,
	useUserSelectListLazyQuery,
	UserSelectListDocument,
} from '@/generated/graphql';
import { useCurrentUser } from '@/features/Auth';
import { AppDropdown } from '@/features/App';
import { useTranslation } from '@/composables';
import { type ValueOf } from '@/types';
import { DEFAULT_FILTER_USER_GROUPS } from './constants';

export type UserSelectListProps = {
	modelValue: SuiSelectListItemsAndGroupsValueType;
	dividerText: string;
	anyUserText?: string;
	noUsersText?: string;
	additionalUserGroups?: DynamicUserGroup[];
	filterUserGroups?: UserStatus[];
	variant?: ValueOf<typeof SuiInputVariant>;
	message?: string;
	showSwitch?: boolean;
	showSelectedUsersFirst?: boolean;
	isMultipleGroupSelectEnabled?: boolean;
};

const props = withDefaults(defineProps<UserSelectListProps>(), {
	anyUserText: undefined,
	noUsersText: undefined,
	additionalUserGroups: () => [],
	filterUserGroups: () => DEFAULT_FILTER_USER_GROUPS,
	variant: undefined,
	message: undefined,
	showSwitch: true,
	showSelectedUsersFirst: false,
	isMultipleGroupSelectEnabled: false,
});

const emit = defineEmits<{
	(
		e: 'update:model-value',
		value: SuiSelectListItemsAndGroupsValueType
	): void;
}>();

const { t } = useTranslation();
const { currentUser } = useCurrentUser();
const {
	result,
	onResult: onResultGroupsAndFilteredUsers,
	loading: loadingGroupsAndFilteredUsers,
} = useUserSelectListQuery({
	usersListInput: {
		filters: {
			statuses: props.filterUserGroups,
		},
	},
	includeGroups: true,
});

const {
	load,
	loading: loadingUsers,
	onResult: onResultUsers,
} = useUserSelectListLazyQuery(
	{
		includeGroups: false,
	},
	{ enabled: props.modelValue.itemIds.length > 0 }
);

const loadUsers = (userIds: Array<User['id']>) => {
	load(UserSelectListDocument, {
		usersListInput: {
			filters: {
				ids: userIds.map(Number),
			},
		},
		includeGroups: false,
	});
};

const initiallySelectedUserIds = ref<Array<User['id']>>([]);
const users = ref<UserSelectListQuery['UsersList']>([]);

const sortSelectedUsersFirst = (users: UserSelectListQuery['UsersList']) => {
	if (initiallySelectedUserIds.value.length > 0) {
		const selectedUserIdsSet = new Set(initiallySelectedUserIds.value);

		const sortedUsers = [...users].sort((a, b) => {
			const aSelected = selectedUserIdsSet.has(a.id);
			const bSelected = selectedUserIdsSet.has(b.id);

			if (aSelected && bSelected) {
				return 0;
			}

			if (!aSelected && !bSelected) {
				return 0;
			}

			return aSelected ? -1 : 1;
		});

		return sortedUsers;
	}

	return users;
};

const anyUserText = props.anyUserText ?? t('select_list_users_groups_any_user');

const showPlaceholderSummaryText = computed(
	() =>
		Boolean(props.noUsersText) &&
		props.modelValue.itemIds.length === 0 &&
		props.modelValue.groupIds.length === 0
);

const isPlaceholderTextErrorVariant = computed(
	() =>
		showPlaceholderSummaryText.value &&
		props.variant === SuiInputVariant.ERROR
);

const summaryTextClassAddons = computed(() => [
	{
		'user-select-list__summary-text--placeholder':
			showPlaceholderSummaryText.value,
		'user-select-list__summary-text--error':
			isPlaceholderTextErrorVariant.value,
	},
]);

const truncatingListGap = computed(() =>
	props.dividerText === ',' ? '0' : '0.5rem'
);

const userGroups = computed<SuiSelectListGroupType[]>(() => {
	const realUserGroups =
		result.value?.UserGroupsList?.map((group) => {
			return {
				id: group.id,
				name: group.name,
				items: group.users
					.filter((user) =>
						props.filterUserGroups.includes(user.status)
					)
					.map((user) => user.id),
			};
		}) || [];

	const sortedRealUserGroups = [...realUserGroups].sort((a, b) =>
		a.name.localeCompare(b.name)
	);

	if (props.additionalUserGroups.length > 0) {
		return [
			{
				id: '',
				name: t('select_list_users_groups_dynamic_groups'),
				type: 'group',
				items: [],
			},
			...props.additionalUserGroups.map((group) => {
				return {
					id: group.id,
					name: group.name,
					items: [],
				};
			}),
			{
				id: '',
				name: t('user_groups'),
				type: 'group',
				items: [],
			},
			...sortedRealUserGroups,
		];
	}

	return sortedRealUserGroups;
});

const getUserGroup = (id: SuiSelectListGroupType['id']) => {
	return userGroups.value.find((group) => group.id === id);
};

const summaryText = computed<string[]>(() => {
	const isUserGroupSelected = props.modelValue.groupIds.length > 0;

	if (isUserGroupSelected) {
		if (
			props.isMultipleGroupSelectEnabled &&
			props.modelValue.groupIds.length
		) {
			const selectedGroups: string[] = [];
			props.modelValue.groupIds.forEach((groupId) => {
				const groupName =
					userGroups.value.find((group) => group.id === groupId)
						?.name || '';
				if (groupName) {
					let groupText = '';
					if (selectedGroups.length > 0) {
						groupText += ` ${props.dividerText} `;
					}
					groupText += groupName;
					selectedGroups.push(groupText);
				}
			});
			if (selectedGroups.length === 0) {
				// if groups have been deleted then we may still have group IDs from props that don't exist anymore
				// we want to display something to the user to indicate the difference from when no groups have been selected
				selectedGroups.push(t('select_list_no_active_groups'));
			}
			return selectedGroups;
		} else {
			return [
				userGroups.value.find(
					(group) => group.id === props.modelValue.groupIds[0]
				)?.name || '',
			];
		}
	}

	const selectedItemCount = props.modelValue.itemIds.length;
	const totalUsersCount = users.value.length;
	const areAllUsersSelected = selectedItemCount === totalUsersCount;

	if (areAllUsersSelected) {
		return [anyUserText];
	}

	if (selectedItemCount > 0) {
		const title: string[] = [];

		props.modelValue.itemIds.forEach((id, index) => {
			let user = '';

			const selectedUserName =
				users.value?.find((user) => user.id === id)?.name || '';

			if (index > 0 && selectedUserName) {
				user += ` ${props.dividerText} `;
			}

			user +=
				currentUser.value?.id === id
					? t('select_list_users_groups_me')
					: selectedUserName;

			title.push(user);
		});

		return title;
	}

	if (props.noUsersText && showPlaceholderSummaryText.value) {
		return [props.noUsersText];
	}

	return [anyUserText];
});

const isLoading = computed(
	() => loadingGroupsAndFilteredUsers.value || loadingUsers.value
);

const onUserToggle = (id: string) => {
	let selectedUsers = props.modelValue.itemIds;

	if (selectedUsers.includes(id)) {
		selectedUsers = selectedUsers.filter((userId) => userId !== id);
	} else {
		selectedUsers = [...selectedUsers, id];
	}

	const value: SuiSelectListItemsAndGroupsValueType = {
		itemIds: selectedUsers,
		groupIds: [],
	};

	emit('update:model-value', value);
};

const onToggleAll = (selectAll: boolean) => {
	const value: SuiSelectListItemsAndGroupsValueType = {
		itemIds: selectAll ? users.value?.map((user) => user.id) : [],
		groupIds: [],
	};

	emit('update:model-value', value);
};

const onToggleAllGroups = (selectAll: boolean) => {
	const value: SuiSelectListItemsAndGroupsValueType = {
		itemIds: [],
		groupIds: selectAll ? userGroups.value.map((group) => group.id) : [],
	};

	emit('update:model-value', value);
};

const onToggleGroupOn = (id: string) => {
	const group = getUserGroup(id);

	if (group && group.items) {
		const value: SuiSelectListItemsAndGroupsValueType = {
			itemIds: [
				...new Set([...props.modelValue.itemIds, ...group.items]),
			],
			groupIds: [],
		};
		emit('update:model-value', value);
	}
};

const onToggleGroupOff = (id: string) => {
	const group = getUserGroup(id);

	if (group?.items) {
		const value = props.modelValue;
		value.itemIds = value.itemIds.filter(
			(item) => !group.items?.includes(item)
		);
		emit('update:model-value', value);
	}
};

const onSelectGroup = (id: string) => {
	const value: SuiSelectListItemsAndGroupsValueType = {
		itemIds: [],
		groupIds: [],
	};

	if (props.isMultipleGroupSelectEnabled) {
		if (props.modelValue.groupIds.includes(id)) {
			value.groupIds = props.modelValue.groupIds.filter(
				(groupId) => groupId !== id
			);
		} else {
			value.groupIds = [...props.modelValue.groupIds, id];
		}
	} else {
		value.groupIds = [id];
	}

	emit('update:model-value', value);
};

onResultGroupsAndFilteredUsers((result) => {
	if (result.data) {
		const fetchedUsers = result.data.UsersList;

		users.value = sortSelectedUsersFirst(fetchedUsers);

		const fetchedUserIds = fetchedUsers.map(({ id }) => id);
		const missingSelectedUserIds = props.modelValue.itemIds.filter(
			(id) => id && !fetchedUserIds.includes(id)
		);

		if (missingSelectedUserIds.length > 0) {
			loadUsers(missingSelectedUserIds);
		}
	}
});

onResultUsers((result) => {
	if (result.data) {
		users.value = sortSelectedUsersFirst([
			...users.value,
			...result.data.UsersList,
		]);
	}
});

onMounted(() => {
	if (props.showSelectedUsersFirst) {
		initiallySelectedUserIds.value = props.modelValue.itemIds;
	}
});
</script>

<style lang="scss">
.user-select-list {
	--sui-horizontal-truncating-list-gap: v-bind(truncatingListGap);

	&__summary-text {
		&--placeholder {
			color: rgba($neutral-90, 0.5);
		}

		&--error {
			color: $red-50;
		}
	}
}
</style>
